import React, { useState, useCallback, useMemo } from 'react';
import type { LoadMoreListProps } from './LoadMoreList.types';

export const DEFAULT_INITIAL_VISIBLE = 10;
export const DEFAULT_LOAD_MORE_COUNT = 10;

function LoadMoreListComponent<T>({
  items,
  initialVisibleCount = DEFAULT_INITIAL_VISIBLE,
  loadMoreCount = DEFAULT_LOAD_MORE_COUNT,
  lookbackCount,
  initialStartIndex,
  children
}: LoadMoreListProps<T>) {
  const [loadMoreStep, setLoadMoreStep] = useState(0);

  const lookback = lookbackCount ?? initialVisibleCount;
  const windowSize = lookback + loadMoreCount;
  const baseStart = initialStartIndex ?? 0;

  const visibleItems = useMemo(() => {
    if (loadMoreStep <= -1) {
      // baseStart === 0: only step -1, show first page.
      if (baseStart === 0) {
        return items.slice(0, Math.min(initialVisibleCount, items.length));
      }
      // baseStart > 0: step -k (k >= 1). Window = loadMoreCount before + lookback overlap with "next" step.
      const k = -loadMoreStep;
      const prevStart = Math.max(0, baseStart - k * loadMoreCount);
      const end = Math.min(
        items.length,
        baseStart - (k - 1) * loadMoreCount + lookback
      );
      return items.slice(prevStart, end);
    }
    if (loadMoreStep === 0) {
      const start = baseStart;
      const end = Math.min(start + initialVisibleCount, items.length);
      return items.slice(start, end);
    }
    const firstStepStart = baseStart + initialVisibleCount - lookback;
    const startIndex = firstStepStart + (loadMoreStep - 1) * loadMoreCount;
    const endIndex = Math.min(startIndex + windowSize, items.length);
    return items.slice(Math.max(0, startIndex), endIndex);
  }, [
    items,
    initialVisibleCount,
    loadMoreCount,
    loadMoreStep,
    windowSize,
    lookback,
    baseStart
  ]);

  const currentStartIndex =
    loadMoreStep <= -1
      ? baseStart > 0
        ? Math.max(0, baseStart - -loadMoreStep * loadMoreCount)
        : 0
      : loadMoreStep === 0
      ? baseStart
      : Math.max(0, baseStart + initialVisibleCount - lookback) +
        (loadMoreStep - 1) * loadMoreCount;

  const hasMore =
    loadMoreStep <= -1
      ? baseStart > 0
        ? baseStart - (-loadMoreStep - 1) * loadMoreCount + lookback <
          items.length
        : items.length > initialVisibleCount
      : loadMoreStep === 0
      ? baseStart + initialVisibleCount < items.length
      : currentStartIndex + windowSize < items.length;

  const hasPrev = currentStartIndex > 0;

  const onLoadMore = useCallback(() => {
    setLoadMoreStep(prev => (prev === -1 && baseStart > 0 ? 1 : prev + 1));
  }, [loadMoreStep, baseStart]);

  const onLoadPrev = useCallback(() => {
    const nextStep = (prev: number) => {
      if (prev === 0) return -1;
      if (prev === 1 && baseStart > 0) return -1;
      return baseStart > 0 ? prev - 1 : Math.max(-1, prev - 1);
    };
    setLoadMoreStep(nextStep);
  }, [baseStart, loadMoreStep]);

  const loadMoreControls = useMemo(
    () => ({ onLoadMore, hasMore, onLoadPrev, hasPrev }),
    [onLoadMore, hasMore, onLoadPrev, hasPrev]
  );

  return <>{children(visibleItems, loadMoreControls)}</>;
}

export function LoadMoreList<T>(props: LoadMoreListProps<T>) {
  return <LoadMoreListComponent {...props} />;
}
